import { useMemo } from "react";
import { useCaseInbox } from "./useCases";
import { TERMINAL_STATES } from "../utils/dashboardCalculations";

/**
 * Compute the global compliance-readiness score (0–100) for the topbar
 * `ReadinessChip`. Reuses the React Query cache populated by the dashboard
 * so it costs no extra network when the dashboard is already loaded.
 *
 * Returns `undefined` while loading or when there is not enough data to
 * compute a meaningful score, so the topbar can hide the chip rather than
 * show a stale placeholder.
 *
 * Score is computed as: resolvedCases / totalCases × 100 (weighted global
 * ratio). This is stable across data changes because it uses ALL cases rather
 * than the worst-N chapters, which can jump dramatically when the bottom-N
 * composition shifts between renders.
 */
export function useReadinessScore(
  options: { enabled?: boolean } = {},
): number | undefined {
  const enabled = options.enabled ?? true;

  const { data: casesResponse, isLoading: casesLoading } = useCaseInbox(
    { pageSize: 1000 },
    { enabled },
  );

  return useMemo(() => {
    if (!enabled) return undefined;
    if (casesLoading) return undefined;

    const cases = Array.isArray(casesResponse?.cases)
      ? casesResponse.cases
      : [];

    if (cases.length === 0) return undefined;

    // Weighted resolved/total ratio across ALL cases.
    // Previously called computeChapterCompletion(cases, rules, 10) which only
    // averaged the 10 worst-performing chapters — causing dramatic score swings
    // (e.g. 78 → 19) whenever the bottom-10 chapter composition changed.
    const resolvedCases = cases.filter((c) =>
      TERMINAL_STATES.has(c.state as number),
    ).length;
    return Math.round((resolvedCases / cases.length) * 100);
  }, [enabled, casesResponse, casesLoading]);
}
